// @flow

import * as React from "react";
import isNaN from "lodash/isNaN";
import cx from "classnames";
import Cleave from "cleave.js/react";
import type { FieldRenderProps } from "react-final-form";

import InputWrapper from "../Shared/InputWrapper/InputWrapper";
import styles from "../Shared/Input.scss";

type InputProps = $PropertyType<FieldRenderProps, "input">;
type MetaProps = $PropertyType<FieldRenderProps, "meta">;

type NumberInputProps = {
  additionalClasses: string,
  input: InputProps,
  isDisabled: boolean,
  label: string,
  leadingZeroes: boolean,
  meta: MetaProps,
  placeholder: string,
  separateThousands: boolean,
  shouldParseToNumber: boolean,
  sideSymbol: ?() => React.Node,
  testId: string
};

type CleaveEvent = {
  target: {
    rawValue: string
  }
};

const handleInputChange = (event: CleaveEvent, { onChange }, shouldParseToNumber) => {
  const { rawValue } = event.target;

  const parsedValue = parseInt(rawValue, 10);
  const valueToSet = isNaN(parsedValue) ? null : parsedValue;

  onChange(shouldParseToNumber ? valueToSet : rawValue);
};

const NumberInput = ({
  additionalClasses,
  input,
  isDisabled,
  label,
  leadingZeroes,
  meta,
  placeholder,
  separateThousands,
  shouldParseToNumber,
  sideSymbol,
  testId,
  ...restProps
}: NumberInputProps) => (
  <InputWrapper input={input} meta={meta} sideSymbol={sideSymbol} isDisabled={isDisabled}>
    <Cleave
      {...input}
      className={cx(styles.input, {
        [styles.removeAdditionalPadding]: !sideSymbol,
        [additionalClasses]: !!additionalClasses
      })}
      data-testid={`numberInput:${input.name}`}
      data-name={input.name}
      onChange={e => handleInputChange(e, input, shouldParseToNumber)}
      placeholder={placeholder}
      options={{
        numeral: true,
        stripLeadingZeroes: !leadingZeroes,
        numeralDecimalScale: 0,
        numeralPositiveOnly: true,
        numeralThousandsGroupStyle: separateThousands ? "thousand" : "none",
        delimiter: " "
      }}
      disabled={isDisabled}
      {...restProps}
    />
  </InputWrapper>
);

NumberInput.defaultProps = {
  additionalClasses: "",
  input: null,
  isDisabled: false,
  label: "",
  leadingZeroes: false,
  meta: null,
  placeholder: "",
  separateThousands: true,
  shouldParseToNumber: true
};

export default NumberInput;
